// WebGL2 particle field engine — 30k particles, vertex-shader morph + pointer + noise.
import { ROBOT_EDGES, GRAPH_EDGES, MAP_EDGES, SPIRAL_EDGES, CONSTELLATION_EDGES, BOM_EDGES, edgesToPoints, bomBarColors } from './shapes.js';

const VERT = `#version 300 es
precision highp float;
in vec2 aPositionR, aPositionG, aPositionM, aPositionS, aPositionC, aPositionB;
in vec3 aColorB;
uniform vec2 uRes;
uniform float uScene; // 0=robot, 1=graph, 2=map, 3=BOM, 4=constellation
uniform float uDt;
uniform vec2 uPointer;
uniform float uPointerActive;

out vec4 vColor;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1,0)), f.x),
             mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), f.x), f.y);
}

void main() {
  float s = uScene;
  vec2 a, b;
  float blend;

  // Scene 0→1: robot→graph, 1→2: graph→map, 2→3: map→BOM, 3→4: BOM→constellation
  if (s < 1.0) { a = aPositionR; b = aPositionG; blend = s; }
  else if (s < 2.0) { a = aPositionG; b = aPositionM; blend = s - 1.0; }
  else if (s < 3.0) { a = aPositionM; b = aPositionB; blend = s - 2.0; }
  else if (s < 4.0) { a = aPositionB; b = aPositionC; blend = s - 3.0; }
  else { a = aPositionC; b = aPositionC; blend = 1.0; }

  vec2 target = mix(a, b, blend);

  float n = noise(target * 2.5 + uDt * 0.3) * 0.06;
  target += vec2(
    cos(n * 12.0 + uDt * 0.4) * 0.008,
    sin(n * 12.0 + uDt * 0.35) * 0.008
  );

  if (uPointerActive > 0.5) {
    vec2 toPtr = target - uPointer;
    float dist = length(toPtr);
    if (dist < 0.35 && dist > 1e-4) {
      float strength = (1.0 - dist / 0.35) * 0.12;
      target += (toPtr / dist) * strength;
    }
  }

  gl_Position = vec4(target, 0.0, 1.0);
  gl_PointSize = mix(2.5, 4.5, 1.0 - blend * 0.3);

  // Color logic: scene-aware
  vec3 sceneColor;
  if (s < 1.0) {
    // Robot → Graph: blue-lavender
    float warm = hash(target * 3.7) * 0.15;
    sceneColor = vec3(
      mix(0.35, 0.85, blend * 0.5) + warm,
      mix(0.40, 0.65, blend * 0.3),
      mix(0.65, 0.95, blend * 0.4)
    );
  } else if (s < 2.0) {
    // Graph → Map: teal-cyan (Bay Area water/land)
    sceneColor = vec3(
      mix(0.30, 0.25, blend),
      mix(0.65, 0.75, blend),
      mix(0.85, 0.90, blend)
    );
  } else if (s < 3.0) {
    // Map → BOM: transition to per-particle BOM colors
    vec3 bomCol = aColorB;
    float t = blend;
    // Fade from map teal into BOM colors
    vec3 fromCol = vec3(0.25, 0.75, 0.90);
    sceneColor = mix(fromCol, bomCol, t);
  } else if (s < 4.0) {
    // BOM → Constellation: keep BOM color, brighten
    vec3 bomCol = aColorB;
    sceneColor = bomCol * (0.85 + blend * 0.35);
  } else {
    // Constellation: BOM colors at full brightness
    sceneColor = aColorB * 1.2;
  }

  vColor = vec4(sceneColor, 0.62 + hash(target * 17.3) * 0.28);
}
`;

const FRAG = `#version 300 es
precision highp float;
in vec4 vColor;
out vec4 fragColor;
void main() {
  vec2 uv = gl_PointCoord - 0.5;
  float d = length(uv);
  float alpha = 1.0 - smoothstep(0.0, 0.5, d * 3.8);
  alpha = pow(alpha, 1.6);
  fragColor = vec4(vColor.rgb, vColor.a * alpha * 0.68);
}
`;

export class WebGL2Engine {
  constructor(canvas, count = 30000) {
    this.canvas = canvas;
    this.count = count;
    this.gl = null;
    this.program = null;
    this._vao = null;
    this._running = false;
    this._rafId = null;
    this._lastTime = 0;

    this._scene = 0;
    this._targetScene = 0;
    this._pointerX = 0;
    this._pointerY = 0;
    this._pointerActive = false;

    this._resizeHandler = this._onResize.bind(this);
    this._pointerHandler = this._onPointer.bind(this);
    this._leaveHandler = () => { this._pointerActive = false; };
    this.backend = 'webgl2';
    this.ok = false;
  }

  init(countOverride) {
    if (countOverride) this.count = countOverride;

    const gl = this.canvas.getContext('webgl2', {
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    });
    if (!gl) return false;
    this.gl = gl;

    const vs = this._compile(VERT, gl.VERTEX_SHADER);
    const fs = this._compile(FRAG, gl.FRAGMENT_SHADER);
    if (!vs || !fs) return false;

    this.program = gl.createProgram();
    gl.attachShader(this.program, vs);
    gl.attachShader(this.program, fs);
    gl.linkProgram(this.program);
    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      console.error('WebGL2 link failed:', gl.getProgramInfoLog(this.program));
      return false;
    }

    this._uRes = gl.getUniformLocation(this.program, 'uRes');
    this._uScene = gl.getUniformLocation(this.program, 'uScene');
    this._uDt = gl.getUniformLocation(this.program, 'uDt');
    this._uPointer = gl.getUniformLocation(this.program, 'uPointer');
    this._uPointerActive = gl.getUniformLocation(this.program, 'uPointerActive');

    // Generate shape point sets
    this._robotPts = edgesToPoints(ROBOT_EDGES, this.count, 0.02);
    this._graphPts = edgesToPoints(GRAPH_EDGES, this.count, 0.01);
    this._mapPts = edgesToPoints(MAP_EDGES, this.count, 0.015);
    this._spiralPts = edgesToPoints(SPIRAL_EDGES, this.count, 0.01);
    this._constPts = edgesToPoints(CONSTELLATION_EDGES, this.count, 0.01);
    this._bomPts = edgesToPoints(BOM_EDGES, this.count, 0.008);
    this._bomColors = bomBarColors(this.count);

    const va = gl.createVertexArray();
    gl.bindVertexArray(va);
    this._vao = va;

    this._uploadBuffer('aPositionR', this._robotPts);
    this._uploadBuffer('aPositionG', this._graphPts);
    this._uploadBuffer('aPositionM', this._mapPts);
    this._uploadBuffer('aPositionS', this._spiralPts);
    this._uploadBuffer('aPositionC', this._constPts);
    this._uploadBuffer('aPositionB', this._bomPts);
    this._uploadBufferVec3('aColorB', this._bomColors);

    gl.bindVertexArray(null);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.blendEquation(gl.FUNC_ADD);

    this._onResize();
    window.addEventListener('resize', this._resizeHandler);
    window.addEventListener('pointermove', this._pointerHandler);
    window.addEventListener('pointerleave', this._leaveHandler);

    this.ok = true;
    return true;
  }

  _compile(src, type) {
    const gl = this.gl;
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error('Shader compile:', gl.getShaderInfoLog(s));
      gl.deleteShader(s);
      return null;
    }
    return s;
  }

  _uploadBuffer(name, data) {
    const gl = this.gl;
    const loc = gl.getAttribLocation(this.program, name);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  }

  _uploadBufferVec3(name, data) {
    const gl = this.gl;
    const loc = gl.getAttribLocation(this.program, name);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 3, gl.FLOAT, false, 0, 0);
  }

  _onResize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    if (this.gl) {
      this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  _onPointer(e) {
    const rect = this.canvas.getBoundingClientRect();
    this._pointerX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this._pointerY = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this._pointerActive = true;
  }

  setScene(v) { this._targetScene = v; }
  setPointer(x, y, active) { this._pointerX = x; this._pointerY = y; this._pointerActive = active; }

  frame(timeSeconds, dt) {
    const gl = this.gl;
    if (!gl) return;
    gl.useProgram(this.program);
    gl.bindVertexArray(this._vao);

    this._scene += (this._targetScene - this._scene) * Math.min(dt * 9, 1);
    gl.uniform2f(this._uRes, this.canvas.width, this.canvas.height);
    gl.uniform1f(this._uScene, this._scene);
    gl.uniform1f(this._uDt, timeSeconds);
    gl.uniform2f(this._uPointer, this._pointerX, this._pointerY);
    gl.uniform1f(this._uPointerActive, this._pointerActive ? 1 : 0);

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.POINTS, 0, this.count);
  }

  start() {
    if (this._running) return;
    this._running = true;
    this._lastTime = performance.now() / 1000;
    const loop = (now) => {
      if (!this._running) return;
      this._rafId = requestAnimationFrame(loop);
      const t = now / 1000;
      const dt = Math.min(t - this._lastTime, 0.1);
      this._lastTime = t;
      this._onResize();
      this.frame(t, dt);
    };
    this._rafId = requestAnimationFrame(loop);
  }

  stop() {
    this._running = false;
    if (this._rafId) cancelAnimationFrame(this._rafId);
  }

  destroy() {
    this.stop();
    window.removeEventListener('resize', this._resizeHandler);
    window.removeEventListener('pointermove', this._pointerHandler);
    window.removeEventListener('pointerleave', this._leaveHandler);
    if (this.gl) {
      const gl = this.gl;
      if (this._vao) gl.deleteVertexArray(this._vao);
      if (this.program) gl.deleteProgram(this.program);
      const ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
    }
    this.gl = null;
  }
}
